import { type TFunction } from 'i18next';

import {
  type LedgerAccountsQueryParams,
  zodLedgerAccountsQueryParamsSchema,
} from '~/data/ledgerAccount';

import {
  createPersistableQueryFilterKeyGuard,
  UNIVERSAL_FILTER_TYPES,
  type FilterDraft,
  type UniversalFiltersPageConfig,
} from '~/modules/universalFilters';

const isPersistableLedgerAccountsFilterKey =
  createPersistableQueryFilterKeyGuard<LedgerAccountsQueryParams>({
    queryKeys: zodLedgerAccountsQueryParamsSchema.keyof().options,
  });

/**
 * Seeds the drawer's isCurrentlyEffective toggle on, so the list opens showing
 * only current, non-deleted account versions.
 */
export const LEDGER_ACCOUNTS_DEFAULT_FILTERS: FilterDraft = [
  {
    field: 'isCurrentlyEffective',
    value: true,
  },
];

export function getLedgerAccountsFilterConfig(
  t: TFunction<'ledgerAccounts'>,
): UniversalFiltersPageConfig<LedgerAccountsQueryParams> {
  return {
    defaultFilters: LEDGER_ACCOUNTS_DEFAULT_FILTERS,
    drawerFields: [
      {
        field: 'isCurrentlyEffective',
        input: 'booleanToggle',
        label: t(($) => $.drawer.isCurrentlyEffective.label),
      },
      {
        field: 'companyIds',
        input: 'companies',
        isMultiSelect: true,
        label: t(($) => $.drawer.companyIds.label),
        placeholder: t(($) => $.drawer.companyIds.placeholder),
      },
      {
        field: 'costTypes',
        input: 'text',
        label: t(($) => $.drawer.costTypes.label),
        placeholder: t(($) => $.drawer.costTypes.placeholder),
      },
      {
        field: 'effectiveAsOf',
        input: 'date',
        label: t(($) => $.drawer.effectiveAsOf.label),
      },
    ],
    mapToPayload: ({ filters, search }) =>
      mapLedgerAccountsFiltersToPayload(filters, search),
    savedFilters: {
      enabled: true,
    },
    search: {
      placeholder: t(($) => $.filtersRow.search.placeholder),
    },
    type: UNIVERSAL_FILTER_TYPES.ledgerAccounts,
  };
}

/**
 * Converts universal filter clauses into ledger-accounts query params.
 * `costTypes` is a single free-text drawer field but the API expects an
 * array. `isCurrentlyEffective` wins over `effectiveAsOf`: once
 * a caller asks for only currently effective rows, a stale `effectiveAsOf`
 * clause from a previous draft must not narrow the result further.
 */
export function mapLedgerAccountsFiltersToPayload(
  filters: FilterDraft,
  search: string,
): Partial<LedgerAccountsQueryParams> {
  const payload: Partial<LedgerAccountsQueryParams> = {};

  for (const filter of filters) {
    switch (filter.field) {
      case 'costTypes': {
        if (typeof filter.value === 'string' && filter.value.trim()) {
          payload.costTypes = [filter.value.trim()];
        }

        break;
      }

      default: {
        if (isPersistableLedgerAccountsFilterKey(filter.field)) {
          Object.assign(payload, { [filter.field]: filter.value });
        }
      }
    }
  }

  if (payload.isCurrentlyEffective) {
    delete payload.effectiveAsOf;
  }

  const trimmedSearch = search.trim();
  if (trimmedSearch) payload.searchString = trimmedSearch;

  return payload;
}
